import { ReviewerData, ReviewStatus } from "@/types/application";
import { DepartmentIcon, UserIcon } from "../icons";
import { Avatar } from "@ui/avatar";
import { ReviewStatusTag } from "../application-status-tag";
import dayjs from "dayjs";
import { cn } from "@/utils/cn";

export const ReviewCard: React.FC<{
  node: ReviewerData;
  className?: string;
}> = ({ node, className }) => {
  const isOnHold = node.eventErrors?.some(
    (event) => event.phase === "taskEnter" || event.phase === "taskLeave",
  );

  return (
    <div
      className={cn(
        "border-stroke flex w-full flex-col overflow-hidden rounded-md border bg-white",
        isOnHold && "border-[1.5px] border-red",
        className,
      )}
    >
      <div className="flex min-h-[66px] items-center gap-4 px-4 py-3">
        <div className="flex flex-1 items-center gap-2">
          {node.type === "user" ? (
            <Avatar
              name={node.assigneeName}
              className="h-11 w-11 text-base font-semibold"
            />
          ) : (
            <div
              className={cn(
                "bg-gray-2 rounded-full h-11 w-11 p-2.5",
                node.status === ReviewStatus.Canceled && "opacity-25",
              )}
            >
              <DepartmentIcon className="text-primary-text" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium leading-6 text-dark">
              {node.assigneeName}
            </div>
            {node.timestamp && (
              <div className="truncate text-xs font-medium leading-5 text-secondary-text">
                {dayjs(node.timestamp).format("YYYY-MM-DD HH:mm")}
              </div>
            )}
          </div>
        </div>
        {node.status !== ReviewStatus.Canceled &&
          (isOnHold ? (
            <div className="inline-flex w-fit items-center rounded-[4px] bg-red-light-6 px-3 py-[3px]">
              <span className="text-sm font-medium leading-[22px] text-red">
                On hold
              </span>
            </div>
          ) : (
            <ReviewStatusTag
              status={node.status}
              className="px-3 py-[3px] [&>span]:text-sm [&>span]:leading-[22px]"
            />
          ))}
      </div>
      {node.comment && (
        <div className="bg-gray px-4 py-2.5 text-xs font-medium leading-[1.4] text-dark">
          {node.comment}
        </div>
      )}
    </div>
  );
};

export const UnassignedReviewCard: React.FC<{
  assignedBy?: string;
  className?: string;
}> = ({ assignedBy, className }) => (
  <div
    className={cn(
      "border-stroke flex h-[68px] w-full items-center overflow-hidden rounded-md border-[1.5px] border-dashed bg-white px-4 py-3",
      className,
    )}
  >
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-2">
        <UserIcon className="h-6 w-6 text-secondary-text" />
      </div>
      <div className="min-w-0 flex-1 font-medium text-secondary-text">
        <div className="truncate text-sm leading-6">Approver TBD</div>
        {assignedBy && (
          <div className="truncate text-xs leading-5">
            Will be assigned by {assignedBy}
          </div>
        )}
      </div>
    </div>
  </div>
);
